import { EmailDocumentCategoryResult } from '../../../services/api/dataServiceOutlookQuickSave';
import { getFetchApiOptions, getEnvironment360BizURL, ApiConfig } from '../../../services/configs/appConfig';
import { getHeaders } from '../../../services/api/dataService';
import { isOutlookApp } from '../../../utils/Helper';
import fetchClient from '../../../services/api/fetchClient';

/**
 * Default category record numbers for document types
 */
enum DefaultCategoryRecno {
    INCOMING_DOC = 110,
    OUTGOING_DOC = 111
}

interface CacheSettingsResponse {
    value: string;
}

/**
 * Fetches a cache setting value from the backend
 * @param settingCategory - The category (e.g., 'mail')
 * @param settingKey - The key (e.g., 'defcat_docout' or 'defcat_docin')
 * @returns Promise resolving to the cache setting value
 */
export const getCacheSettingValue = async (settingCategory: string, settingKey: string): Promise<string> => {
    const headers = await getHeaders();
    try {
        const response = await fetchClient(getEnvironment360BizURL() + ApiConfig.GetValueFromSettings, {
            body: JSON.stringify({
                owner: settingCategory,
                key: settingKey
            }),
            headers: headers,
            method: "POST",
            ...getFetchApiOptions()
        }) as CacheSettingsResponse;
        return response.value || '';
    } catch {
        return '';
    }
};

/**
 * Gets the current email sender's email address from Office.js context
 * @returns The sender's email address
 */
export const getCurrentEmailSender = (): string => {
    try {
        return Office?.context?.mailbox?.item?.sender?.emailAddress || '';
    } catch {
        return '';
    }
};

/**
 * Gets the current user's profile email address from Office.js context
 * @returns The user's email address
 */
export const getCurrentUserEmail = (): string => {
    try {
        return Office?.context?.mailbox?.userProfile?.emailAddress || '';
    } catch {
        return '';
    }
};

/**
 * Helper function to get the appropriate fallback category for Outlook context
 */
const getFallbackCategoryForOutlook = async (
    categories: EmailDocumentCategoryResult[],
    senderEmail: string,
    userEmail: string
): Promise<EmailDocumentCategoryResult | null> => {
    // Determine which cache setting to use based on sender
    const isOutgoing = senderEmail.toLowerCase() === userEmail.toLowerCase();
    const settingKey = isOutgoing ? 'defcat_docout' : 'defcat_docin';

    // Get the cache setting value (fails silently and returns empty string on any backend error)
    const categoryRecno = await getCacheSettingValue('mail', settingKey);

    // If backend call succeeded and returned a valid category ID
    if (categoryRecno) {
        const matchingCategory = categories.find(cat => cat.Recno.toString() === categoryRecno);
        if (matchingCategory) {
            return matchingCategory;
        }
    }

    // If backend fails or no matching category found, try fallback categories
    const fallbackRecno = isOutgoing ? DefaultCategoryRecno.OUTGOING_DOC : DefaultCategoryRecno.INCOMING_DOC;
    const fallbackCategory = categories.find(cat => cat.Recno === Number(fallbackRecno));
    return fallbackCategory || null;
};

/**
 * Helper function to get category for non-Outlook context
 */
const getCategoryForNonOutlook = (categories: EmailDocumentCategoryResult[]): EmailDocumentCategoryResult | null => {
    const priorityCategory = categories.find(cat => cat.Recno === Number(DefaultCategoryRecno.OUTGOING_DOC));
    return priorityCategory || null;
};

/**
 * Gets the prioritized category based on email sender conditions and cache settings
 * @param categories - Array of available categories
 * @returns Promise resolving to the selected category or null
 */
export const getPrioritizedCategory = async (categories: EmailDocumentCategoryResult[]): Promise<EmailDocumentCategoryResult | null> => {
    try {
        // If there's only one category, return it
        if (categories.length === 1) {
            return categories[0];
        }

        // If no categories available, return null
        if (categories.length === 0) {
            return null;
        }
        if (!isOutlookApp()) {
            return getCategoryForNonOutlook(categories);
        }

        // If in Outlook, use email sender logic with backend calls
        const senderEmail = getCurrentEmailSender();
        const userEmail = getCurrentUserEmail();

        if (senderEmail && userEmail) {
            return await getFallbackCategoryForOutlook(categories, senderEmail, userEmail);
        }

        return null;

    } catch {
        return null;
    }
};